import firebase from 'firebase/app'
import 'firebase/auth'
import authorize from './authorizations'

export const SCOPES = ['email', 'profile']

/**
 Authentification (SignIn & Authorization) With Firebase App

 Constructor:
 - config : Object      | Required | Firebase Config ('apiKey', 'authDomain', 'projectId', 'appId') and 'application' must be specified
 - app    : FirebaseApp | Optional | If not set, the lib initialize a "DEFAULT" Firebase App

 Methods:
 - signIn()        : return Promise<FirebaseAuth> when user is signed in
 - authorization() : return Authorized User object. Reject if user isn't authorized
 **/
export default class FirebaseAuth {
  constructor (config, app, dev = false) {
    this.config = this.checkConfig(config)
    // Firebase v8: We initialize or recover the existing app
    if (app) {
      this.app = app
    } else {
      // Avoid "Firebase App named '[DEFAULT]' already exists"
      this.app = !firebase.apps.length ? firebase.initializeApp(config) : firebase.app()
    }
    this.dev = dev
  }

  signIn () {
    return this.signInFirebase()
      .then((firebaseUser) => (this.firebaseUser = firebaseUser))
      .then(() => this)
  }

  signInFirebase () {
    return new Promise((resolve, reject) => {
      const unsubscribe = firebase.auth().onAuthStateChanged((firebaseUser) => {
        try {
          unsubscribe()
          if (firebaseUser) {
            resolve(firebaseUser)
          } else {
            // no user => directly redirect to signIn
            const provider = new firebase.auth.SAMLAuthProvider('saml.pass')
            
            // SignIn with popup in case of PWA on mobile. In this case, if redirect is too quick, the browser think it's an ads
            if (window.matchMedia('(display-mode: standalone)').matches) {
              console.log('display-mode is standalone')
              return firebase
                .auth()
                .signInWithPopup(provider)
                .catch((error) => {
                  console.log(error)
                  console.log('Unable to signIn with popup, try with redirect')
                  return firebase.auth().signInWithRedirect(provider)
                })
            }
            return firebase.auth().signInWithRedirect(provider)
          }
        } catch (error) {
          reject(error)
        }
      })
    })
  }

  authorization () {
    const application = this.config.application
    const firebaseUser = this.firebaseUser
    if (!application || !firebaseUser) throw 'No auth user, or application is not confiured'
    return firebaseUser
      .getIdToken(true)
      .then((idToken) => authorize(idToken, application, this.dev))
      .then(async (userInfos) => {
        this.roles = await this.firebaseUser.getIdTokenResult(true).then((idTokenResult) => idTokenResult.claims.roles)
        this.user = userInfos
      })
      .catch((error) => {
        const { roles, userInfos } = error.context
        throw { roles, userInfos, firebaseUser: this.firebaseUser }
      })
      .then(() => this)
  }

  checkConfig (config) {
    if (!config) throw 'No config specified'
    for (let attribut of ['apiKey', 'authDomain', 'projectId', 'appId', 'application']) {
      if (!config[attribut]) {
        throw `Config: attribute ${attribut} is missing`
      }
    }

    for (let scope of SCOPES) {
      if (!config.scopes || !config.scopes.includes(scope)) {
        config.scopes = config.scopes || []
        config.scopes.push(scope)
      }
    }
    return config
  }
}



// WEBPACK FOOTER //
// ./src/lib/auth/firebaseAuth.js